/**
 * Pure JSON-path tree walker. Split out of `jsonStructureAggregatorStore.ts`
 * (S8b.T1b) so `inferJsonStructure.ts` — and, transitively, the Data Import
 * suggestion engine (`electron/data-import/metaInfoSuggestionRules.ts`) —
 * can reuse it without pulling the renderer-only zustand store + the
 * `metaInfoApi` IPC client (`window.electronAPI`) into the electron
 * main-process bundle. The main-process Vite build has no `@` alias
 * resolution (see `electron/data-import/fixedValueGate.ts`'s doc comment),
 * so anything crossing into `electron/` must resolve every import via a
 * relative path — this file does. No behavior change from the original:
 * `jsonStructureAggregatorStore.ts` re-exports `walkJsonPaths` from here for
 * its own existing callers.
 */
import type { JsonStructureCachePathType } from '../../../../types/electron';

function kindOf(value: unknown): JsonStructureCachePathType {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  const t = typeof value;
  if (t === 'object') return 'object';
  if (t === 'string') return 'string';
  if (t === 'number') return 'number';
  if (t === 'boolean') return 'boolean';
  return 'string';
}

/**
 * Walk a parsed JSON value and emit each path it reaches with the type at
 * that path. Descends through both objects and arrays — array elements are
 * enumerated under `${basePath}[*]`, so a column whose values are
 * `[{x: 1}, {x: 2}]` produces `$[*]` (object) and `$[*].x` (number). The
 * `[*]` notation matches the wildcard syntax the SQL emit understands, so
 * paths picked from the peek popover translate directly into `any =` /
 * `path_eq` predicates. The root path `$` is intentionally not emitted
 * (it would always be 100% and would just bloat the cache).
 *
 * Element types are unioned per array — if elements vary (e.g. some
 * strings, some numbers) the array-element path is emitted once per
 * distinct type observed, and the aggregator's pathTypes set folds them
 * into a `'mixed'` label.
 *
 * Cycle-safe: a `WeakSet` of visited objects is threaded through the
 * recursion so cyclic graphs (e.g. `o.self = o`) emit each reachable path
 * once instead of stack-overflowing.
 */
export function walkJsonPaths(
  value: unknown,
  visit: (path: string, type: JsonStructureCachePathType) => void,
  basePath = '$',
  visited: WeakSet<object> = new WeakSet(),
): void {
  if (value === null || typeof value !== 'object') return;
  if (visited.has(value as object)) return;
  visited.add(value as object);

  if (Array.isArray(value)) {
    const elemPath = `${basePath}[*]`;
    const elementTypes = new Set<JsonStructureCachePathType>();
    for (const elem of value) {
      elementTypes.add(kindOf(elem));
      if (elem !== null && typeof elem === 'object') {
        walkJsonPaths(elem, visit, elemPath, visited);
      }
    }
    // Emit the wildcard path itself once per distinct element type seen.
    // The aggregator dedupes within a sample (`seenInThisRow`) but folds
    // multiple types into `'mixed'` at flush time.
    for (const t of elementTypes) {
      visit(elemPath, t);
    }
    return;
  }

  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const path = `${basePath}.${k}`;
    const type = kindOf(v);
    visit(path, type);
    if (v !== null && typeof v === 'object') {
      walkJsonPaths(v, visit, path, visited);
    }
  }
}
